<template>
  <w-modal
    :value="show"
    :size="modalSize"
    :custom-content-class="modalContentClass"
    :style="modalStyleVars"
    close-button="Anuluj"
    submit-button="Zapisz"
    :title="title"
    :close-on-submit="false"
    :hide-on-overlay="false"
    :hide-on-esc="false"
    @submit="save"
    @close="close"
  >
    <div class="row document-value-modal">
      <div class="form-group col-12">
        <w-vue-select
          v-if="$schema && !$schema.$model.id"
          :model="$schema.code"
          :values="values"
          @input="tryGetCurrentValues"
        />

        <component :is="currentCustomEditor" v-if="$schema && currentCustomEditor" :schema="$schema" />

        <template v-else-if="$schema">
          <div v-for="(date, index) in $schema.value.dates.$items" :key="'value_date_' + index" class="row">
            <w-datepicker class="pt-2 col-10" :model="date.$item.date" />

            <div class="col-2 pt-5">
              <w-button
                v-if="$schema.value.dates.$items.length > 1"
                class="btn-danger"
                icon="fas fa-trash mr-0"
                aria-label="Usuń wartość"
                @click="$schema.value.dates.$removeRow(index)"
              />
            </div>
          </div>

          <div v-for="(value, index) in $schema.value.numbers.$items" :key="'value_number_' + index" class="row">
            <w-input class="pt-2 col-10" :model="value.$item.number" />

            <div class="col-2 pt-5">
              <w-button
                v-if="$schema.value.numbers.$items.length > 1"
                class="btn-danger"
                icon="fas fa-trash mr-0"
                aria-label="Usuń wartość"
                @click="$schema.value.numbers.$removeRow(index)"
              />
            </div>
          </div>

          <w-input
            v-if="$schema.code.$model && $schema.code.$model.indexOf('_TEXT') >= 0"
            :model="$schema.value.text"
          />

          <yes-or-no-radio
            v-if="$schema.code.$model && $schema.code.$model.startsWith('IS_')"
            :model="$schema.value.bool"
          />
        </template>
      </div>
    </div>
  </w-modal>
</template>

<script>
import { mapState } from 'vuex';

import YesOrNoRadio from '@/components/forms/document/YesOrNoRadio.vue';
import { documentValueTranslation } from '@/filters/documentValue';
import { manageMixin } from '@/mixins/manage';
import schema from '@/schemas/documentValue.schema';
import { tryParseInt } from '@/utils/number';

import {
  createEmptyDocumentValueForCode,
  getDocumentValueEditorConfig,
  normalizeDocumentValueByCode,
  prepareDocumentValueSchemaByCode
} from './documentValueEditors';

const emptyData = () => ({
  id: null,
  code: null,
  value: { date: null, dates: [], number: null, numbers: [], bool: null, text: null, ranges: [], totalDays: 0 }
});

// @vuese
// Modal dodania wartości dokumentu
// @group Document
export default {
  components: { YesOrNoRadio },
  mixins: [manageMixin('documentValue', {}, emptyData)],
  schema,
  props: {
    // Kody edytowalnych wartości dokumentów
    codes: { type: Array, required: true }
  },
  data() {
    return {
      model: {}
    };
  },
  computed: {
    currentCode() {
      return this.$schema && this.$schema.code ? this.$schema.code.$model : null;
    },
    customEditorConfig() {
      return getDocumentValueEditorConfig(this.currentCode);
    },
    currentCustomEditor() {
      return this.customEditorConfig && this.customEditorConfig.component;
    },
    modalSize() {
      return this.customEditorConfig && this.customEditorConfig.modal && this.customEditorConfig.modal.size
        ? this.customEditorConfig.modal.size
        : '';
    },
    modalMinHeight() {
      const minHeight =
        this.customEditorConfig && this.customEditorConfig.modal && this.customEditorConfig.modal.minHeight;

      if (typeof minHeight === 'number') {
        return minHeight > 0 ? `${minHeight}px` : '';
      }

      return typeof minHeight === 'string' ? minHeight.trim() : '';
    },
    modalContentClass() {
      return this.modalMinHeight ? 'document-value-modal-content' : '';
    },
    modalStyleVars() {
      return this.modalMinHeight ? { '--document-value-modal-min-height': this.modalMinHeight } : undefined;
    },
    values() {
      return (Array.isArray(this.codes) ? this.codes : [])
        .map(code => {
          const normalizedCode = typeof code === 'string' ? code.trim() : code;

          return {
            value: normalizedCode,
            text: this.documentValueTranslation(normalizedCode)
          };
        })
        .sort((a, b) => (a.text > b.text ? 1 : a.text == b.text ? 0 : -1));
    },
    title() {
      const currentCode = this.currentCode;
      if (!this.id) {
        return 'Dodawanie wartości dokumentu';
      }

      return currentCode
        ? `Edycja wartości dokumentu: ${this.documentValueTranslation(currentCode)}`
        : 'Edycja wartości dokumentu';
    },
    ...mapState({
      stateData: state => {
        const source = state.documentValue.data || {};
        const value = {
          ...emptyData().value,
          ...normalizeDocumentValueByCode(source.code, source.value || {})
        };

        value.dates = (value.dates || []).map(item => ({ ...item, added: false }));
        value.numbers = (value.numbers || []).map(item => ({ ...item, added: false }));

        return {
          ...source,
          value
        };
      }
    })
  },
  watch: {
    show(value) {
      if (value) {
        this.setDocumentIdIfPossible();
      }
    }
  },
  mounted() {
    this.setDocumentIdIfPossible();
  },
  methods: {
    documentValueTranslation,
    setDocumentIdIfPossible() {
      if (this.$schema && this.$schema.documentId) {
        this.$schema.documentId.$setData(tryParseInt(this.$route.params.documentId));
      }
    },
    prepareCustomEditorSchema(code) {
      if (!this.$schema) {
        return;
      }

      if (code && getDocumentValueEditorConfig(code)) {
        prepareDocumentValueSchemaByCode(code, this.$schema);
      }
    },
    // @vuese
    // Pobierz wartości jeżeli istnieją
    tryGetCurrentValues() {
      if (!this.$schema) {
        return;
      }

      const code = this.currentCode;
      if (code) {
        this.$schema.value.$setDefault();
        this.$store
          .dispatch('documentValue/find', { documentId: this.$schema.documentId.$model, code })
          .then(item => {
            if (item) {
              const normalizedValue = {
                ...emptyData().value,
                ...normalizeDocumentValueByCode(code, item.value || {})
              };

              normalizedValue.dates = normalizedValue.dates || [];
              normalizedValue.numbers = normalizedValue.numbers || [];
              item.id = null;
              this.$schema.$setData({ ...item, value: normalizedValue }, undefined, true);
            } else {
              this.$schema.value.$setData(
                createEmptyDocumentValueForCode(code, { ...emptyData().value }),
                undefined,
                true
              );
            }
          })
          .then(() => {
            if (getDocumentValueEditorConfig(code)) {
              this.prepareCustomEditorSchema(code);
            } else if (code.indexOf('_TEXT') >= 0) {
              return;
            } else if (code.indexOf('DATE') >= 0) {
              this.$schema.value.dates.$addRow({ added: true });
            } else if (code.indexOf('NUMBER') >= 0) {
              this.$schema.value.numbers.$addRow({ added: true });
            }
          });
      }
    }
  }
};
</script>

<style>
.document-value-modal-content .modal-body {
  min-height: var(--document-value-modal-min-height);
}

.document-value-modal .btn.btn-danger .mr-0 {
  margin-right: 0 !important;
}
</style>
